import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, useApi, type Registration, type RegistrationTier } from '../lib/api';
import { formatRelative } from '../lib/edgeStatus';
import { ChevronRight, RefreshCw, Check, X, KeyRound, Loader2, ShieldAlert } from 'lucide-react';

// Registration lifecycle statuses are disjoint from home provisioning statuses,
// so they get their own dot/label maps rather than widening StatusBadge. Same
// visual language: neutral pill, saturated dot carries the meaning.
const STATUS_DOT: Record<string, string> = {
  pending_review: 'bg-warn-attention',
  approved_provisioning: 'bg-live-sync',
  key_sent: 'bg-live-sync',
  key_claimed: 'bg-live-sync',
  paired: 'bg-armed-ok',
  rejected: 'bg-slate-400',
};

const STATUS_LABELS: Record<string, string> = {
  pending_review: 'pending review',
  approved_provisioning: 'approved · provisioning',
  key_sent: 'key sent',
  key_claimed: 'key claimed',
  rejected: 'rejected',
};

// User-facing tier names (the code values stay 'shared'/'dedicated').
const TIER_LABELS: Record<string, string> = {
  shared: 'Standard (shared)',
  dedicated: 'Dedicated',
};

function RegistrationStatusPill({ status }: { status: string }) {
  const dot = STATUS_DOT[status] ?? 'bg-slate-400';
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 bg-white text-xs font-medium text-ink-800">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// Same rule the create-home API enforces.
const HOME_ID_REGEX = /^[a-zA-Z0-9]{3,32}$/;

// Suggest a homeId in the fleet's existing style ("720 Front Rd, Pickering" →
// "720FrontRd"). Prefers the street part of the address; falls back to the
// requester's name. Empty string when nothing sane comes out — admin types one.
function suggestHomeId(reg: Registration): string {
  const source = reg.address?.trim() ? reg.address.split(',')[0] : (reg.homeName ?? reg.name ?? '');
  const id = (source ?? '')
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('')
    .slice(0, 32);
  return HOME_ID_REGEX.test(id) ? id : '';
}

// Derive a readable display name from the camelCase home id ("10SelbyDr" → "10 Selby Dr").
// Mirrors CreateHomePage's helper.
function deriveDisplayName(homeId: string): string {
  if (!homeId) return '';
  return homeId
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([0-9])([A-Z])/g, '$1 $2');
}

function formatAuditTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function RegistrationsPage() {
  const api = useApi();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Approve/reject/send-key failures land here (e.g. the send-key 409 while a
  // dedicated home is still provisioning) — dismissible, separate from the poll error.
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [approving, setApproving] = useState<Registration | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const { registrations } = await api.listRegistrations();
      setRegistrations(registrations);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 10000);
    return () => clearInterval(id);
  }, []);

  const onSendKey = async (r: Registration, resend: boolean) => {
    if (
      resend &&
      !confirm(`Resend the key email to ${r.email}? The previous link stops working immediately.`)
    ) {
      return;
    }
    setBusyId(r.registrationId);
    setActionError(null);
    try {
      await api.sendRegistrationKey(r.registrationId);
      await refresh();
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async (e: FormEvent) => {
    e.preventDefault();
    if (!rejectingId) return;
    setBusyId(rejectingId);
    setActionError(null);
    try {
      await api.rejectRegistration(rejectingId, rejectReason.trim());
      setRejectingId(null);
      setRejectReason('');
      await refresh();
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-brand-ink">Registrations</h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Self-registration requests from mymanor.click/register (and the CLI/installer).
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-ink px-3 py-1.5 rounded-lg border border-slate-300 hover:border-slate-400"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}
      {actionError && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm flex items-start justify-between gap-3">
          <span>{actionError}</span>
          <button
            onClick={() => setActionError(null)}
            className="text-red-700 hover:text-red-800 p-0.5 rounded hover:bg-red-100"
            aria-label="Dismiss error"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-ink-800 uppercase text-xs tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Requested</th>
              <th className="text-left px-4 py-3 font-medium">Name / Email</th>
              <th className="text-left px-4 py-3 font-medium">Tier</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Home</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {registrations.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-600">
                  No registration requests yet. They arrive from mymanor.click/register.
                </td>
              </tr>
            )}
            {registrations.map((r) => {
              const busy = busyId === r.registrationId;
              return (
                <Fragment key={r.registrationId}>
                  <tr className="border-t border-slate-200 hover:bg-slate-100">
                    <td className="px-4 py-3 text-ink-800 text-xs" title={r.createdAt}>
                      {formatRelative(r.createdAt)}
                      <div className="text-[10px] text-slate-500 mt-0.5">via {r.source}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-brand-ink">{r.homeName ?? r.name ?? 'New home'}</div>
                      <div className="text-xs text-slate-600">{r.email}</div>
                      {r.address && (
                        <div className="text-xs text-slate-500 truncate max-w-[240px]" title={r.address}>
                          {r.address}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-800">
                      <div>{TIER_LABELS[r.approvedTier ?? r.tier] ?? (r.approvedTier ?? r.tier)}</div>
                      {r.approvedTier && (
                        <div className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                          Approved tier
                          {r.approvedTier !== r.tier
                            ? ` · requested ${TIER_LABELS[r.tier] ?? r.tier}`
                            : ''}
                        </div>
                      )}
                      {r.sharedTierCapOverride && (
                        <details className="mt-1.5 max-w-[260px] text-left">
                          <summary className="cursor-pointer font-mono text-[10px] font-semibold uppercase tracking-wide text-alert-breach">
                            Security exception
                          </summary>
                          <div className="mt-1.5 rounded-md border border-alert-breach/30 bg-alert-breach/5 p-2 text-[11px] leading-4 text-ink-800">
                            <p className="font-semibold text-alert-breach">Standard cap overridden</p>
                            <dl className="mt-1 space-y-1">
                              <div>
                                <dt className="inline font-medium">Reason: </dt>
                                <dd className="inline">{r.sharedTierCapOverride.reason}</dd>
                              </div>
                              <div>
                                <dt className="inline font-medium">Reviewed: </dt>
                                <dd className="inline">
                                  {r.sharedTierCapOverride.reviewedBy} ·{' '}
                                  {formatAuditTime(r.sharedTierCapOverride.reviewedAt)}
                                </dd>
                              </div>
                              <div>
                                <dt className="inline font-medium">Capacity before approval: </dt>
                                <dd className="inline">
                                  {r.sharedTierCapOverride.reservedHomesBefore}/
                                  {r.sharedTierCapOverride.maxHomes} reserved
                                </dd>
                              </div>
                              {r.sharedTierCapOverride.reservedHomeIdsBefore.length > 0 && (
                                <div>
                                  <dt className="inline font-medium">Reserved homes: </dt>
                                  <dd className="inline font-mono">
                                    {r.sharedTierCapOverride.reservedHomeIdsBefore.join(', ')}
                                  </dd>
                                </div>
                              )}
                            </dl>
                          </div>
                        </details>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <RegistrationStatusPill status={r.status} />
                      {r.status === 'rejected' && r.rejectReason && (
                        <div className="text-xs text-slate-500 mt-1 truncate max-w-[200px]" title={r.rejectReason}>
                          {r.rejectReason}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.homeId ? (
                        <Link
                          to={`/homes/${r.homeId}`}
                          className="inline-flex items-center gap-0.5 font-mono text-xs text-slate-600 hover:text-brand-ink"
                        >
                          {r.homeId} <ChevronRight size={12} />
                        </Link>
                      ) : (
                        <span className="text-slate-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {r.status === 'pending_review' && (
                          <>
                            <button
                              onClick={() => setApproving(r)}
                              disabled={busy}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-ink hover:bg-ink-800 text-brand-bone text-xs font-medium disabled:opacity-50"
                            >
                              <Check size={12} /> Approve
                            </button>
                            <button
                              onClick={() => {
                                setRejectingId(r.registrationId);
                                setRejectReason('');
                              }}
                              disabled={busy}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 text-xs disabled:opacity-50"
                            >
                              <X size={12} /> Reject
                            </button>
                          </>
                        )}
                        {r.status === 'approved_provisioning' && (
                          <button
                            onClick={() => onSendKey(r, false)}
                            disabled={busy}
                            title="Emails the one-time key link. Fails cleanly (409) until the home finishes provisioning."
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-ink-800 hover:bg-slate-200 text-xs disabled:opacity-50"
                          >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
                            Send key
                          </button>
                        )}
                        {(r.status === 'key_sent' || r.status === 'key_claimed') && (
                          <button
                            onClick={() => onSendKey(r, true)}
                            disabled={busy}
                            title="Mints a fresh link and invalidates the previous one"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-ink-800 hover:bg-slate-200 text-xs disabled:opacity-50"
                          >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
                            Resend key
                          </button>
                        )}
                        {(r.status === 'paired' || r.status === 'rejected') && (
                          <span className="text-slate-500 text-xs">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {rejectingId === r.registrationId && (
                    <tr className="border-t border-slate-200 bg-slate-50">
                      <td colSpan={6} className="px-4 py-3">
                        <form onSubmit={onReject} className="flex items-center gap-2">
                          <span className="text-xs text-slate-600 shrink-0">
                            Reject {r.email}?
                          </span>
                          <input
                            autoFocus
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="reason (kept internal — no email is sent)"
                            className="flex-1 bg-white border border-slate-300 rounded px-3 py-1.5 text-sm text-brand-ink placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
                          />
                          <button
                            type="submit"
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 text-xs disabled:opacity-50"
                          >
                            <X size={12} /> {busy ? 'Rejecting…' : 'Reject'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setRejectingId(null);
                              setRejectReason('');
                            }}
                            disabled={busy}
                            className="px-2.5 py-1.5 rounded-lg text-xs text-slate-600 hover:text-brand-ink border border-slate-300 hover:border-slate-400 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </form>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {approving && (
        <ApproveModal
          registration={approving}
          onClose={() => setApproving(null)}
          onApproved={async () => {
            setApproving(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function ApproveModal({
  registration,
  onClose,
  onApproved,
}: {
  registration: Registration;
  onClose: () => void;
  onApproved: () => void;
}) {
  const api = useApi();
  const suggestion = suggestHomeId(registration);
  const [tier, setTier] = useState<RegistrationTier>(registration.tier);
  const [homeId, setHomeId] = useState(suggestion);
  const [displayName, setDisplayName] = useState(registration.homeName ?? deriveDisplayName(suggestion));
  const [overrideSharedTierCap, setOverrideSharedTierCap] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on Escape — small UX nicety expected of any modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const id = homeId.trim();
    if (!HOME_ID_REGEX.test(id)) {
      setError('Home ID must be 3–32 letters/digits, camelCase (e.g. 10SelbyDr)');
      return;
    }
    const trimmedOverrideReason = overrideReason.trim();
    if (
      tier === 'shared' &&
      overrideSharedTierCap &&
      (trimmedOverrideReason.length < 10 || trimmedOverrideReason.length > 500)
    ) {
      setError('Security exception reason must be 10–500 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const baseApproval = {
        tier,
        homeId: id,
        displayName: displayName.trim() || deriveDisplayName(id),
      };
      await api.approveRegistration(
        registration.registrationId,
        tier === 'shared' && overrideSharedTierCap
          ? {
              ...baseApproval,
              overrideSharedTierCap: true,
              overrideReason: trimmedOverrideReason,
            }
          : baseApproval,
      );
      onApproved();
    } catch (err: any) {
      setError(
        err instanceof ApiError && err.code === 'SHARED_TIER_CAP_REACHED'
          ? 'The Standard trial is at its one-home cap. Choose Dedicated, or open the Security exception below only after a risk review.'
          : (err.message ?? 'Approve failed'),
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm py-10 px-4"
      onClick={(e) => {
        // Click on backdrop closes; clicks on the inner card don't bubble here.
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="approve-registration-modal-title"
        className="w-full max-w-lg bg-white border border-slate-200 rounded-xl shadow-glow"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 id="approve-registration-modal-title" className="text-lg font-semibold text-brand-ink">
            Approve registration
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-slate-600 hover:text-brand-ink p-1 rounded hover:bg-slate-100 disabled:opacity-40"
            aria-label="Close approve registration dialog"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          <div className="text-sm text-ink-800">
            {registration.homeName ?? registration.name ?? 'New home'} · <span className="text-slate-600">{registration.email}</span>
            {registration.address && (
              <div className="text-xs text-slate-500 mt-0.5">{registration.address}</div>
            )}
          </div>

          <Field label="Tier" hint={`requested ${TIER_LABELS[registration.tier] ?? registration.tier}`}>
            <select
              value={tier}
              onChange={(e) => {
                setTier(e.target.value as RegistrationTier);
                setError(null);
              }}
              disabled={submitting}
              className={fieldClass}
            >
              <option value="shared">Standard — shared online environment</option>
              <option value="dedicated">Dedicated — per-home isolated</option>
            </select>
            <div
              className={`mt-2 rounded-md border p-3 text-xs leading-5 ${
                tier === 'shared'
                  ? 'border-warn-attention/40 bg-warn-attention/5 text-ink-800'
                  : 'border-slate-200 bg-slate-50 text-slate-600'
              }`}
            >
              <p className="font-semibold text-brand-ink">
                {tier === 'shared'
                  ? 'Controlled trial · normal limit: one home'
                  : 'Per-home isolated environment'}
              </p>
              <p className="mt-1">
                {tier === 'shared'
                  ? 'Uses shared online infrastructure and does not support recordings. A hard reservation cap keeps normal Standard operation to one home.'
                  : 'Provisions a separate AWS account for this home (~10–20 min); the key email sends automatically when provisioning completes.'}
              </p>
            </div>
          </Field>

          <Field label="Home ID" hint="camelCase, 3–32 chars (e.g. 10SelbyDr)">
            <input
              required
              pattern="^[a-zA-Z0-9]{3,32}$"
              value={homeId}
              onChange={(e) => setHomeId(e.target.value)}
              disabled={submitting}
              placeholder="10SelbyDr"
              className={fieldClass}
            />
          </Field>

          <Field label="Display name" hint="shown to the owner in the MANOR app">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={submitting}
              placeholder={deriveDisplayName(homeId) || 'e.g. 10 Selby Dr'}
              className={fieldClass}
            />
          </Field>

          {tier === 'shared' && (
            <details className="group rounded-lg border border-alert-breach/30 bg-alert-breach/5">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-xs font-semibold text-alert-breach [&::-webkit-details-marker]:hidden">
                <ShieldAlert size={15} aria-hidden="true" />
                Security exception · override Standard cap
                <ChevronRight
                  size={14}
                  className="ml-auto transition-transform group-open:rotate-90"
                  aria-hidden="true"
                />
              </summary>
              <div className="border-t border-alert-breach/20 px-3 py-3 text-xs leading-5 text-ink-800">
                <p className="font-semibold text-alert-breach">Do not use for unrelated customers.</p>
                <p className="mt-1">
                  Standard homes trust the same user pool. Approving another home can let a
                  signed-in user from one home reach another home&apos;s cameras. Alarm and
                  integration data also share backend resources.
                </p>
                <p className="mt-1 font-medium">
                  This is a single reviewed exception. Further Standard approvals remain blocked
                  until the shared account is manually reconciled and reviewed again.
                </p>
                <label className="mt-3 flex items-start gap-2 rounded-md border border-alert-breach/30 bg-white p-2.5">
                  <input
                    type="checkbox"
                    checked={overrideSharedTierCap}
                    onChange={(e) => {
                      setOverrideSharedTierCap(e.target.checked);
                      setError(null);
                    }}
                    disabled={submitting}
                    className="mt-0.5 h-4 w-4 accent-alert-breach"
                  />
                  <span>I understand the cross-home camera risk and need to override the cap.</span>
                </label>
                {overrideSharedTierCap && (
                  <label className="mt-3 block">
                    <span className="font-medium text-brand-ink">Audit reason</span>
                    <span className="ml-1 text-slate-600">(required, 10–500 characters)</span>
                    <textarea
                      required
                      minLength={10}
                      maxLength={500}
                      rows={3}
                      value={overrideReason}
                      onChange={(e) => {
                        setOverrideReason(e.target.value);
                        setError(null);
                      }}
                      disabled={submitting}
                      placeholder="Name the reviewed exception, affected homes, and why Dedicated cannot be used."
                      className={`${fieldClass} mt-1 resize-y`}
                    />
                    <span className="mt-1 block text-right font-mono text-[10px] text-slate-500">
                      {overrideReason.trim().length}/500
                    </span>
                  </label>
                )}
              </div>
            </details>
          )}

          {error && (
            <div className="p-2.5 rounded bg-red-50 border border-red-200 text-red-700 text-xs">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 -mx-5 px-5 -mb-4 pb-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 rounded text-sm text-ink-800 hover:text-brand-ink border border-slate-300 hover:border-slate-400 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm bg-brand-ink hover:bg-ink-800 text-brand-bone border border-brand-ink disabled:opacity-40"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {submitting
                ? 'Approving…'
                : tier === 'shared' && overrideSharedTierCap
                  ? 'Approve with security exception'
                  : 'Approve'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const fieldClass =
  'w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-sm text-brand-ink placeholder:text-slate-500 focus:border-brand-ink focus:outline-none disabled:opacity-50';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-medium text-brand-ink">{label}</span>
        {hint && <span className="text-[10px] text-slate-600">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
